'use client';

import { Button } from '@pickleballinc/react-ui';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  useSaveOptMultiplePlayers,
  useSaveOptOutMultiplePlayers,
  useSaveRemoveMultiplePlayers,
} from '@/modules/league/new-components/rsvp-components/hooks';
import { LeagueGameDayHeaderDetails, Player } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';

interface HeadingProps {
  cardName: string;
  players: Player[];
  isAuthorized: boolean | null;
  playersForOptOut: number;
  playersForOptIn: number;
  playersForRemove: number;
  playersForWait: number;
  isOptInButtonDisabled: boolean;
  notPlayingPlayers: Player[];
  playingPlayers: Player[];
  waitlistPlayers: Player[];
  playersFromNotToWait: Player[];
  matchDateId: string;
  setPlayersForOptIn: React.Dispatch<React.SetStateAction<Player[]>>;
  setPlayersForOptOut: React.Dispatch<React.SetStateAction<Player[]>>;
  setPlayersForRemove: React.Dispatch<React.SetStateAction<Player[]>>;
  setPlayersForWait: React.Dispatch<React.SetStateAction<Player[]>>;
  canMoveToWait: boolean;
  canOptIn: boolean;
  isLocked: boolean;
  isPartnerLeague: boolean;
  chargeOptIn: boolean;
  chargeOptInURL: string;
  leagueId: string;
  slug: string;
  sessionId: string;
  userUuid?: string;
}

export const Heading = ({
  cardName,
  players,
  isAuthorized,
  playersForOptOut,
  playersForOptIn,
  playersForRemove,
  playersForWait,
  isOptInButtonDisabled,
  notPlayingPlayers,
  playingPlayers,
  waitlistPlayers,
  playersFromNotToWait,
  matchDateId,
  setPlayersForOptIn,
  setPlayersForOptOut,
  setPlayersForRemove,
  setPlayersForWait,
  canMoveToWait,
  canOptIn,
  isLocked,
  isPartnerLeague,
  chargeOptIn,
  chargeOptInURL,
  leagueId,
  slug,
  sessionId,
  userUuid,
}: HeadingProps) => {
  const [showMultiple, setShowMultiple] = useState(false);
  const [showOneOptIn, setShowOneOptIn] = useState(false);

  const { setLoading } = useLoader();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { mutate: optInMultiplePlayers } = useSaveOptMultiplePlayers();
  const { mutate: optOutMultiplePlayers } = useSaveOptOutMultiplePlayers();
  const { mutate: removeMultiplePlayers } = useSaveRemoveMultiplePlayers();

  const isDisabledButtonFromClick =
    (isOptInButtonDisabled && !canMoveToWait && !isAuthorized) ||
    (playersForOptOut === 0 && cardName === 'Playing') ||
    (playersForOptIn === 0 && cardName === 'Not Playing' && playersForWait === 0) ||
    (cardName === 'Waitlist' && playersForRemove === 0);

  useEffect(() => {
    setLoading(false);
  }, [players]);

  const allPlayersHavePartners =
    cardName === 'Not Playing' &&
    isPartnerLeague &&
    players.some((player) => player.PlayerFullName_2 !== '' && player.UserID_2 !== null);
  const showButton =
    isAuthorized &&
    !isLocked &&
    ((cardName === 'Playing' && players.length > 0) ||
      (cardName === 'Waitlist' && players.length > 0 && canMoveToWait) ||
      (cardName === 'Not Playing' &&
        players.length > 0 &&
        (canOptIn || (!canOptIn && canMoveToWait)) &&
        (!isPartnerLeague || allPlayersHavePartners)));

  return (
    <>
      <div className='!sticky top-11 md:top-0 flex justify-between gap-0 bg-white p-4'>
        <h2 className='font-inter text-base font-semibold'>
          {cardName} ({players.length})
        </h2>

        {showButton ? (
          <Button
            variant='primary'
            className={`rounded-md border !bg-leagues-700 !px-3 !py-1 !text-xs font-semibold uppercase text-white ${isDisabledButtonFromClick ? 'cursor-text' : 'cursor-pointer hover:!border-gray-900 hover:!bg-white hover:!text-gray-900'}`}
            disabled={isDisabledButtonFromClick}
            onClick={() => {
              // If the manager selects a single player for opt-in and they pay per game day,
              // show `setShowOneOptIn` and redirect to PBrackets only if the manager is opting in his user
              if (
                cardName === 'Not Playing' &&
                canOptIn &&
                playersForOptIn === 1 &&
                chargeOptIn &&
                userUuid?.toLowerCase() === notPlayingPlayers[0]?.UserID_1?.toLowerCase()
              ) {
                setShowOneOptIn(true);
              } else {
                setShowMultiple(true);
              }
            }}
          >
            {cardName === 'Playing'
              ? `OPT OUT (${playersForOptOut})`
              : cardName === 'Not Playing' && canOptIn
                ? `OPT IN (${playersForOptIn})`
                : cardName === 'Not Playing' && !canOptIn && canMoveToWait
                  ? `WAIT (${playersForWait})`
                  : cardName === 'Waitlist'
                    ? `Remove (${playersForRemove})`
                    : ''}
          </Button>
        ) : null}
      </div>
      <ConfirmationModal
        open={showMultiple}
        onClose={() => setShowMultiple(false)}
        showEachSentenceInNewLine={cardName === 'Not Playing' && canOptIn ? true : false}
        onConfirm={() => {
          if (cardName === 'Not Playing' && canOptIn) {
            setLoading(true);
            optInMultiplePlayers(
              { matchDateId, players: notPlayingPlayers },
              {
                onSuccess: (data) => {
                  if (data.message === 'Opted in successfully') {
                    toast.success(data.message);
                  } else {
                    toast.error(data.message);
                    setLoading(false);
                  }
                  setPlayersForOptIn([]);
                  queryClient.invalidateQueries(['league_game_day_header']);
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          } else if (cardName === 'Playing') {
            setLoading(true);
            optOutMultiplePlayers(
              { leagueId, slug, sessionId, matchDateId, players: playingPlayers },
              {
                onSuccess: (data) => {
                  if (data.message === 'Opted out successfully') {
                    toast.success(data.message);
                  } else {
                    toast.error(data.message);
                    setLoading(false);
                  }
                  setPlayersForOptOut([]);
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          } else if (cardName === 'Not Playing' && !canOptIn && canMoveToWait) {
            setLoading(true);
            removeMultiplePlayers(
              { matchDateId, players: playersFromNotToWait, doing: 'in' },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  setPlayersForWait([]);
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error(`An error occurred while moving players to waitlist.`);
                  setLoading(false);
                },
              }
            );
          } else {
            setLoading(true);
            removeMultiplePlayers(
              { matchDateId, players: waitlistPlayers, doing: 'out' },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  setPlayersForRemove([]);
                  setPlayersForWait([]);
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error(`An error occurred while removing players from waitlist.`);
                  setLoading(false);
                },
              }
            );
          }
          setShowMultiple(false);
        }}
        title='Are you sure?'
        message={
          cardName === 'Not Playing' && canOptIn
            ? `This will opt in : ${playersForOptIn}
						${playersForOptIn > 1 ? ' players' : ' player'}. ${chargeOptIn ? `Because you've selected multiple players, the checkout process will be skipped. If players owe a balance for the game day, they can pay by clicking on their notification icon.` : ''}`
            : cardName === 'Playing'
              ? `This will opt out : ${playersForOptOut}
						${playersForOptOut > 1 ? ' players' : ' player'}.`
              : cardName === 'Not Playing' && !canOptIn && canMoveToWait
                ? `This will move : ${playersForWait}
						${playersForWait > 1 ? ' players to waitlist' : ' player'} to waitlist.`
                : `This will remove : ${playersForRemove}
						${playersForRemove > 1 ? ' players' : ' player'} from waitlist.`
        }
        confirmButtonText='Yes'
      />
      <ConfirmationModal
        open={showOneOptIn}
        onClose={() => setShowOneOptIn(false)}
        onConfirm={() => {
          router.push(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/${chargeOptInURL}`);
          setShowOneOptIn(false);
        }}
        title='Are you sure?'
        message={`This will opt in : ${playersForOptIn} player.`}
        confirmButtonText='Yes'
      />
    </>
  );
};

export const MaxPlayersInfo = ({
  cardName,
  gameDayInfo,
}: {
  cardName: string;
  gameDayInfo: LeagueGameDayHeaderDetails;
}) => (
  <p className='px-4 text-xs font-medium text-gray-500'>
    {cardName === 'Playing'
      ? `Max. # of players ${gameDayInfo.maxPlayers !== -1 ? `(${gameDayInfo.maxPlayers})` : 'unlimited'}`
      : cardName === 'Waitlist'
        ? moment.utc(gameDayInfo.WaitList_OptInToDateTime).format('MMM DD h:mm A')
        : ''}
  </p>
);
